import type {
  BoardCell,
  ChipColor,
  KingsCrownPlayer,
  KingsCrownState,
  KingsCrownViewerHandSlot,
  KingsCrownViewerState,
  KingsCrownViewerTableSlot,
  NumberChip,
  PlacedBoardToken,
  TableChipSlot,
  ValidationResult,
} from "../types";
import {
  KINGS_CROWN_BOARD_SIZE,
  KINGS_CROWN_CENTER,
  KINGS_CROWN_CHIP_NUMBERS,
  KINGS_CROWN_MAX_HAND_SIZE,
  KINGS_CROWN_TABLE_SLOT_COUNT,
} from "../types";

export const ok: ValidationResult = { valid: true };

export function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

export function getDefaultSeed(playerIds: readonly string[]): number {
  return playerIds
    .join("|")
    .split("")
    .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 2166136261);
}

function nextRandom(seed: number): number {
  return (seed * 1664525 + 1013904223) >>> 0;
}

export function shuffle<T>(items: readonly T[], seed: number): readonly T[] {
  const result = [...items];
  let cursor = seed >>> 0;
  for (let index = result.length - 1; index > 0; index -= 1) {
    cursor = nextRandom(cursor);
    const swapIndex = cursor % (index + 1);
    const current = result[index];
    const swap = result[swapIndex];
    if (current === undefined || swap === undefined) {
      throw new Error("Shuffle index out of bounds");
    }
    result[index] = swap;
    result[swapIndex] = current;
  }
  return result;
}

export function createChipBag(): readonly NumberChip[] {
  return (["gold", "gray"] as const).flatMap((chipColor) =>
    KINGS_CROWN_CHIP_NUMBERS.map((number) => ({
      chipId: `${chipColor}-${number}`,
      chipColor,
      number,
    })),
  );
}

export function createBoard(): readonly (readonly BoardCell[])[] {
  return Array.from({ length: KINGS_CROWN_BOARD_SIZE }, (_, row) =>
    Array.from(
      { length: KINGS_CROWN_BOARD_SIZE },
      (_, col): BoardCell =>
        row === KINGS_CROWN_CENTER && col === KINGS_CROWN_CENTER
          ? "CENTER"
          : null,
    ),
  );
}

export function createTableSlots(): readonly TableChipSlot[] {
  return Array.from({ length: KINGS_CROWN_TABLE_SLOT_COUNT }, (_, index) => ({
    slotId: `slot-${index}`,
    chip: null,
    face: null,
    trackIndex: index,
    state: "future" as const,
  }));
}

export function toCellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

export function parseCellId(
  cellId: string,
): { readonly row: number; readonly col: number } | null {
  const match = /^r([0-4])c([0-4])$/.exec(cellId);
  if (!match) return null;
  const row = Number(match[1]);
  const col = Number(match[2]);
  return { row, col };
}

export function getOtherPlayerId(
  state: KingsCrownState,
  playerId: string,
): string {
  const otherPlayerId = state.playerOrder.find(
    (candidate) => candidate !== playerId,
  );
  if (!otherPlayerId) {
    throw new Error("Kings Crown requires exactly two players");
  }
  return otherPlayerId;
}

export function getPlayer(state: KingsCrownState, playerId: string) {
  return state.players.find((player) => player.playerId === playerId);
}

export function isPlacedToken(
  cell: BoardCell | undefined,
): cell is PlacedBoardToken {
  return cell !== undefined && cell !== null && cell !== "CENTER";
}

function getCell(
  board: readonly (readonly BoardCell[])[],
  row: number,
  col: number,
): BoardCell | undefined {
  return board[row]?.[col];
}

export function getOrthogonalNeighbors(
  board: readonly (readonly BoardCell[])[],
  row: number,
  col: number,
): readonly PlacedBoardToken[] {
  return [
    getCell(board, row - 1, col),
    getCell(board, row + 1, col),
    getCell(board, row, col - 1),
    getCell(board, row, col + 1),
  ].filter(isPlacedToken);
}

export function chipsConnect(chip: NumberChip, neighbor: NumberChip): boolean {
  if (chip.chipColor === neighbor.chipColor) {
    return Math.abs(chip.number - neighbor.number) === 1;
  }
  return chip.number === neighbor.number;
}

export function isLegalPlacement(
  state: KingsCrownState,
  chip: NumberChip,
  cellId: string,
): boolean {
  const parsed = parseCellId(cellId);
  if (!parsed) return false;
  const { row, col } = parsed;
  const cell = getCell(state.board, row, col);
  if (cell !== null) return false;

  const neighbors = getOrthogonalNeighbors(state.board, row, col);
  if (neighbors.length === 0) return true;
  return neighbors.some((neighbor) => chipsConnect(chip, neighbor.chip));
}

export function getLegalTargetsForChip(
  state: KingsCrownState,
  chip: NumberChip,
): readonly string[] {
  return state.board.flatMap((rowCells, row) =>
    rowCells.flatMap((_, col) => {
      const cellId = toCellId(row, col);
      return isLegalPlacement(state, chip, cellId) ? [cellId] : [];
    }),
  );
}

export function hasAnyLegalPlacement(
  state: KingsCrownState,
  playerId: string,
): boolean {
  const player = getPlayer(state, playerId);
  if (!player) return false;
  return player.hand.some(
    (chip) => getLegalTargetsForChip(state, chip).length > 0,
  );
}

export function placeTokenOnBoard(
  board: readonly (readonly BoardCell[])[],
  cellId: string,
  token: PlacedBoardToken,
): readonly (readonly BoardCell[])[] {
  const parsed = parseCellId(cellId);
  if (!parsed) return board;
  return board.map((rowCells, row) =>
    rowCells.map((cell, col) =>
      row === parsed.row && col === parsed.col ? token : cell,
    ),
  );
}

export interface BingoResult {
  readonly winnerId: string | null;
  readonly line: readonly string[];
}

function boardLines(): readonly (readonly string[])[] {
  const rows = Array.from({ length: KINGS_CROWN_BOARD_SIZE }, (_, row) =>
    Array.from({ length: KINGS_CROWN_BOARD_SIZE }, (_, col) =>
      toCellId(row, col),
    ),
  );
  const cols = Array.from({ length: KINGS_CROWN_BOARD_SIZE }, (_, col) =>
    Array.from({ length: KINGS_CROWN_BOARD_SIZE }, (_, row) =>
      toCellId(row, col),
    ),
  );
  const downRight = Array.from({ length: KINGS_CROWN_BOARD_SIZE }, (_, index) =>
    toCellId(index, index),
  );
  const upRight = Array.from({ length: KINGS_CROWN_BOARD_SIZE }, (_, index) =>
    toCellId(KINGS_CROWN_BOARD_SIZE - 1 - index, index),
  );
  return [...rows, ...cols, downRight, upRight];
}

export function findBingo(state: KingsCrownState): BingoResult {
  for (const playerId of state.playerOrder) {
    for (const line of boardLines()) {
      const cells = line.map((cellId) => {
        const parsed = parseCellId(cellId);
        if (!parsed) return null;
        return getCell(state.board, parsed.row, parsed.col) ?? null;
      });
      const containsCenter = cells.includes("CENTER");
      const ownedCount = cells.filter(
        (cell) => isPlacedToken(cell) && cell.ownerId === playerId,
      ).length;
      if (
        (!containsCenter && ownedCount === 5) ||
        (containsCenter && ownedCount === 4)
      ) {
        return { winnerId: playerId, line };
      }
    }
  }
  return { winnerId: null, line: [] };
}

function redactTableSlot(slot: TableChipSlot): KingsCrownViewerTableSlot {
  return {
    slotId: slot.slotId,
    chip: slot.face === "up" ? slot.chip : null,
    face: slot.face,
    trackIndex: slot.trackIndex,
    state: slot.state,
  };
}

export function buildLegalTargetsByChipId(
  state: KingsCrownState,
  viewerPlayerId: string,
): Readonly<Record<string, readonly string[]>> {
  if (state.phase !== "PLACE" || state.currentPlayerId !== viewerPlayerId) {
    return {};
  }
  const player = getPlayer(state, viewerPlayerId);
  if (!player) return {};
  return Object.fromEntries(
    player.hand.map((chip) => [
      chip.chipId,
      getLegalTargetsForChip(state, chip),
    ]),
  );
}

export function buildPlayerHandSlots(
  player: KingsCrownPlayer,
): readonly (NumberChip | null)[] {
  const chipsById = new Map(player.hand.map((chip) => [chip.chipId, chip]));
  const baseSlots = (player.handSlots ?? player.hand)
    .slice(0, KINGS_CROWN_MAX_HAND_SIZE)
    .map((slot) => (slot && chipsById.has(slot.chipId) ? slot : null));
  const slottedChipIds = new Set(
    baseSlots.flatMap((slot) => (slot ? [slot.chipId] : [])),
  );
  const slots = [...baseSlots];

  for (const chip of player.hand) {
    if (slottedChipIds.has(chip.chipId)) continue;
    const emptyIndex = slots.indexOf(null);
    if (emptyIndex >= 0) {
      slots[emptyIndex] = chip;
    } else if (slots.length < KINGS_CROWN_MAX_HAND_SIZE) {
      slots.push(chip);
    }
    slottedChipIds.add(chip.chipId);
  }

  return Array.from(
    { length: KINGS_CROWN_MAX_HAND_SIZE },
    (_, index) => slots[index] ?? null,
  );
}

function buildViewerHandSlots(
  player: KingsCrownPlayer | undefined,
): readonly KingsCrownViewerHandSlot[] {
  if (!player) return [];
  return buildPlayerHandSlots(player).map((chip, slotIndex) => ({
    slotIndex,
    chip,
  }));
}

export function buildViewerState(
  state: KingsCrownState,
  viewerPlayerId: string | "observer",
): KingsCrownViewerState {
  const viewerPlayer =
    viewerPlayerId === "observer"
      ? undefined
      : getPlayer(state, viewerPlayerId);
  const opponent = state.players.find(
    (player) => player.playerId !== viewerPlayer?.playerId,
  );
  const draftStatus =
    state.phase === "DRAFT_DRAW"
      ? "needsDraw"
      : state.phase === "DRAFT_PICK"
        ? "needsPick"
        : state.phase === "DRAFT_CLOSE"
          ? "closing"
          : null;

  return {
    gameId: state.gameId,
    viewerPlayerId: viewerPlayer?.playerId ?? "observer",
    phase: state.phase,
    currentPlayerId: state.currentPlayerId,
    players: state.players.map((player) => ({
      playerId: player.playerId,
      remainingCount: player.hand.length,
      isCurrent: player.playerId === state.currentPlayerId,
    })),
    board: state.board,
    table: state.table.map(redactTableSlot),
    bagCount: state.bag.length,
    ownHand: viewerPlayer?.hand ?? [],
    ownHandSlots: buildViewerHandSlots(viewerPlayer),
    opponentHandCount: opponent?.hand.length ?? 0,
    draftStatus,
    legalTargetsByChipId:
      viewerPlayer === undefined
        ? {}
        : buildLegalTargetsByChipId(state, viewerPlayer.playerId),
    winnerId: state.winnerId,
    winLine: state.winLine,
  };
}

export function buildViewerStates(
  state: KingsCrownState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, KingsCrownViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, buildViewerState(state, player.playerId)] as const,
  );
  const playerIds = new Set(state.players.map((player) => player.playerId));
  const observerEntries = [...new Set(observerUserIds)]
    .filter((observerUserId) => !playerIds.has(observerUserId))
    .map(
      (observerUserId) =>
        [observerUserId, buildViewerState(state, "observer")] as const,
    );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}

export function isChipColor(value: unknown): value is ChipColor {
  return value === "gold" || value === "gray";
}
